// @ts-expect-error
import { parse } from 'cookie';
import nookies from 'nookies';
import Crypto from '@/utils/crypto';
import { getEnvironmentDetails } from '@/utils/env';
import { arrayWrap } from '@/utils/object';
import ICookies from './Cookie.types';

/**
 * see the {@link node_modules/next/dist/compiled/@edge-runtime/cookies/index.d.ts}
 */
interface CookieSerializeOptions {
  domain?: string | undefined;
  expires?: Date | undefined;
  httpOnly?: boolean | undefined;
  maxAge?: number | undefined;
  path?: string | undefined;
  priority?: 'low' | 'medium' | 'high' | undefined;
  sameSite?: true | false | 'lax' | 'strict' | 'none' | undefined;
  secure?: boolean | undefined;
}

type TNextCookies = {
  cookies?: Record<string, any>;
  cookie?: Record<string, any>;
  clearCookie?: Function;
};

type TCookieName = keyof ICookies;
type TCookieValue<K extends TCookieName> = ICookies[K] | null;

type TCookieRequest = TServerSideRequest & TNextCookies;
type TCookieResponse = TServerSideResponse & TNextCookies;
type TCookieContext = { req: TCookieRequest; res: TCookieResponse };

export type TCookieOptions = Partial<CookieSerializeOptions> & {
  rawName?: boolean;
  shouldUseCompression?: boolean;
};

const defaultCookieOptions: TCookieOptions = {
  httpOnly: true,
  sameSite: getEnvironmentDetails().isDevelopmentEnvironment ? 'lax' : 'none',
  secure: getEnvironmentDetails().isDevelopmentEnvironment ? false : true,
  maxAge: undefined,
  path: '/',
};

//-----------------------------------------------------------
const { encrypt, decrypt, compressAndEncrypt, decompressAndDecrypt } = Crypto<
  TCookieValue<any>
>(`${process.env.SSR_COOKIE_SECRET}`);
const getCookieName = (
  cookieName: string,
  options?: Pick<TCookieOptions, 'rawName'>
): string => {
  if (options?.rawName) {
    delete options.rawName;
    return cookieName;
  }

  const encoded: string = `${Math.abs(
    cookieName.split('').reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0)
  )}`;

  return encoded;
};

export function setSSRCookie<K extends TCookieName>(
  res: TCookieResponse,
  cookieName: K,
  rawValue: NonNullable<TCookieValue<K>>,
  options?: TCookieOptions
): void {
  const key: string = getCookieName(cookieName, options);
  const value = options?.shouldUseCompression
    ? compressAndEncrypt(rawValue)
    : encrypt(rawValue);
  options = { ...defaultCookieOptions, ...(options || {}) };

  if (typeof res?.cookies?.set === 'function') {
    res.cookies.set(key, value, options);
  } else if (typeof res?.cookie === 'function') {
    res.cookie(key, value, options);
  } else {
    nookies.set({ res }, key, value, options);
  }
}

export function getSSRCookie<K extends TCookieName>(
  reqOrContext: TCookieRequest | TCookieContext,
  cookieName: K,
  options?: Pick<TCookieOptions, 'rawName' | 'shouldUseCompression'>
): TCookieValue<K> {
  const req =
    (reqOrContext as TCookieContext)?.req || (reqOrContext as TCookieRequest);
  const res = (reqOrContext as TCookieContext)?.res || undefined;

  const key: string = getCookieName(cookieName, options);
  let resValue: string | undefined;
  if (typeof res?.getHeader === 'function') {
    const resCookies = arrayWrap(res.getHeader('set-cookie') as string[])
      .filter((v: string) => !!v?.trim())
      .map((v: string) => parse(v))
      .reverse();
    const index = resCookies.findIndex((obj: Record<string, string>) =>
      Object.keys(obj).includes(key)
    );
    if (index > -1) {
      resValue = resCookies?.[index]?.[key] || '';
    }
  }

  let value: string | undefined;
  if (typeof resValue !== 'undefined') {
    value = resValue;
  } else {
    if (typeof req?.cookies?.get === 'function') {
      value = req.cookies.get(key);
    } else if (typeof req?.cookies !== 'undefined') {
      value = req.cookies?.[key];
    } else {
      value = nookies.get({ req })?.[key];
    }
  }

  try {
    return options?.shouldUseCompression
      ? decompressAndDecrypt(value!)!
      : decrypt(value!)!;
  } catch (e) {
    return null;
  }
}

export function deleteSSRCookie(
  res: TCookieResponse,
  cookieName: TCookieName,
  options?: TCookieOptions
): void {
  const key: string = getCookieName(cookieName, options);

  options = { ...defaultCookieOptions, ...(options || {}) };

  if (typeof res?.cookies?.delete === 'function') {
    res.cookies.delete(key, options);
  } else if (typeof res?.clearCookie === 'function') {
    res.clearCookie(key, options);
  } else {
    nookies.destroy({ res }, key, options);
  }
}
